import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ClipboardList, Check, Download, Trash2, Share2, FileDown, Archive, FileText, Calendar, Clock, X } from 'lucide-react';
import ShareButton from '../common/ShareButton';
import { postsAPI, commentsAPI, uploadAPI, aiAPI, usersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import { Card, Button, StatusBadge } from '../ui';
import Loading from '../common/Loading';
import ImageViewer from './ImageViewer';
import CommentsList from './CommentsList';
import VersionHistory from './VersionHistory';
import PlatformPreview from './previews';
import ProductArticleDetailsCard from './ProductArticleDetailsCard';
import CarouselItemsManager from './CarouselItemsManager';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm, getMediaTypeFromUrl, isValidMedia } from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES, isVideoContent } from '../../utils/platformConfig';

const PostReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isClient, isDesigner, isWriter, isAdmin } = useAuth();
  const { isRTL } = useLanguage();
  const { agency } = useWhiteLabel();
  const [showPreview, setShowPreview] = useState(false);

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showUploadImage, setShowUploadImage] = useState(false);
  const [showEditCaption, setShowEditCaption] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingCaption, setEditingCaption] = useState(false);
  const [newCaption, setNewCaption] = useState('');
  const [shareUrl, setShareUrl] = useState(null);
  const [shareUrlCopied, setShareUrlCopied] = useState(false);
  const [generatingShareLink, setGeneratingShareLink] = useState(false);
  const [generatingAICaption, setGeneratingAICaption] = useState(false);
  const [copiedText, setCopiedText] = useState(null); // Track which text was copied
  const [showEditPost, setShowEditPost] = useState(false);
  const [editingPost, setEditingPost] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: ''
  });
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeRequestText, setChangeRequestText] = useState('');
  const [showVersionNotesModal, setShowVersionNotesModal] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');
  const [pendingImageUrl, setPendingImageUrl] = useState(null);
  const [pendingCarouselItems, setPendingCarouselItems] = useState([]);
  const [editingCarouselItems, setEditingCarouselItems] = useState(false);
  const [manageCarouselItems, setManageCarouselItems] = useState([]);
  const [savingCarouselItems, setSavingCarouselItems] = useState(false);
  const [designers, setDesigners] = useState([]);
  const [writers, setWriters] = useState([]);
  const [editingDesigner, setEditingDesigner] = useState(false);
  const [editingWriter, setEditingWriter] = useState(false);
  const [updatingAssignment, setUpdatingAssignment] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduling, setScheduling] = useState(false);

  const handleSaveAsDraft = async () => {
    try {
      setActionLoading(true);
      await postsAPI.update(id, { status: 'draft' });
      showToast.success(isRTL ? 'تم الحفظ كمسودة' : 'Saved as draft');
      navigate('/posts');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchPostData();
    if (isAdmin) {
      fetchUsers();
    }
  }, [id, isAdmin]);

  const fetchUsers = async () => {
    try {
      const [designersResponse, writersResponse] = await Promise.all([
        usersAPI.getByRole('designer'),
        usersAPI.getByRole('writer'),
      ]);
      setDesigners(designersResponse.data.users || []);
      setWriters(writersResponse.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchPostData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [postResponse, commentsResponse, versionsResponse] = await Promise.all([
        postsAPI.getById(id),
        commentsAPI.getByPost(id),
        postsAPI.getVersions(id),
      ]);

      console.log('Post data:', postResponse.data);
      console.log('Comments:', commentsResponse.data);
      console.log('Versions:', versionsResponse.data);

      const postData = postResponse.data.post;
      
      // Fix image URL if it's relative
      if (postData.current_image_url) {
        postData.current_image_url = getImageUrl(postData.current_image_url);
      }

      // Set share URL if share_token exists
      if (postData.share_token && !shareUrl) {
        const url = `${window.location.origin}/post/${postData.share_token}`;
        setShareUrl(url);
      }

      setPost(postData);
      setComments(commentsResponse.data.comments || []);
      setVersions(versionsResponse.data.versions || []);
      
      // Initialize carousel items for management
      if (postData.content_type === 'carousel' && postData.current_carousel_items) {
        setManageCarouselItems(postData.current_carousel_items);
      }
    } catch (err) {
      console.error('Error fetching post data:', err);
      console.error('Error details:', err.response?.data);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من تعميد هذا البوست؟' : 'Are you sure you want to approve this post?',
      isRTL
    );
    
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await postsAPI.approve(id);
      await fetchPostData();
      showToast.success(isRTL ? 'تم تعميد البوست بنجاح!' : 'Post approved successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = () => {
    setShowChangeRequestModal(true);
  };

  const handleSubmitChangeRequest = async () => {
    if (!changeRequestText.trim()) {
      showToast.error(isRTL ? 'يرجى كتابة التعديلات المطلوبة' : 'Please enter the changes needed');
      return;
    }

    try {
      setActionLoading(true);
      await postsAPI.reject(id, { comment: changeRequestText.trim() });
      await fetchPostData();
      setShowChangeRequestModal(false);
      setChangeRequestText('');
      showToast.success(isRTL ? 'تم طلب التعديلات بنجاح!' : 'Changes requested successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelChangeRequest = () => {
    setShowChangeRequestModal(false);
    setChangeRequestText('');
  };

  const handleMarkAsReadyForReview = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من أن البوست جاهز للمراجعة؟' 
        : 'Are you sure this post is ready for review?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(true);
      // Update post status to 'pending'
      await postsAPI.update(id, { status: 'pending' });
      await fetchPostData();
      showToast.success(isRTL ? 'تم تحديث الحالة بنجاح!' : 'Post marked as ready for review!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduledDate || !scheduledTime) {
      showToast.error(isRTL ? 'يرجى تحديد التاريخ والوقت' : 'Please select both date and time');
      return;
    }

    try {
      setScheduling(true);
      // Combine date and time
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      const now = new Date();
      
      if (scheduledDateTime <= now) {
        showToast.error(isRTL ? 'يجب أن يكون التاريخ والوقت في المستقبل' : 'Date and time must be in the future');
        setScheduling(false);
        return;
      }

      // Update post with scheduled_at
      await postsAPI.update(id, { scheduled_at: scheduledDateTime.toISOString() });
      await fetchPostData();
      setShowScheduleModal(false);
      setScheduledDate('');
      setScheduledTime('');
      showToast.success(isRTL ? 'تم جدولة البوست بنجاح!' : 'Post scheduled successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setScheduling(false);
    }
  };

  const handleUnschedule = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من إلغاء الجدولة؟' 
        : 'Are you sure you want to unschedule this post?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(true);
      await postsAPI.update(id, { scheduled_at: null });
      await fetchPostData();
      showToast.success(isRTL ? 'تم إلغاء الجدولة بنجاح!' : 'Post unscheduled successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // Initialize schedule modal with existing scheduled_at if available
  useEffect(() => {
    if (post?.scheduled_at) {
      const scheduledDate = new Date(post.scheduled_at);
      setScheduledDate(scheduledDate.toISOString().split('T')[0]);
      setScheduledTime(scheduledDate.toTimeString().slice(0, 5));
    }
  }, [post?.scheduled_at]);

  const handleCopyShareLink = async () => {
    try {
      // إذا لم يكن الرابط موجوداً، قم بإنشائه أولاً
      if (!shareUrl) {
        setGeneratingShareLink(true);
        const response = await postsAPI.generateShareLink(id);
        const url = `${window.location.origin}/post/${response.data.share_token}`;
        setShareUrl(url);
        setGeneratingShareLink(false);
        
        // نسخ الرابط بعد إنشائه
        await navigator.clipboard.writeText(url);
        setShareUrlCopied(true);
        setTimeout(() => setShareUrlCopied(false), 2000);
        return;
      }

      // إذا كان الرابط موجوداً، قم بنسخه مباشرة
      await navigator.clipboard.writeText(shareUrl);
      setShareUrlCopied(true);
      setTimeout(() => setShareUrlCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const urlToCopy = shareUrl || `${window.location.origin}/post/${post?.share_token}`;
      const textArea = document.createElement('textarea');
      textArea.value = urlToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareUrlCopied(true);
      setTimeout(() => setShareUrlCopied(false), 2000);
    }
  };

  const handleShareWhatsApp = async () => {
    try {
      let url = shareUrl;

      // Generate link if not exists
      if (!url) {
        setGeneratingShareLink(true);
        try {
          const response = await postsAPI.generateShareLink(id);
          url = `${window.location.origin}/post/${response.data.share_token}`;
          setShareUrl(url);
        } catch (err) {
          showToast.error(isRTL ? 'فشل إنشاء الرابط' : 'Failed to generate link');
          setGeneratingShareLink(false);
          return;
        } finally {
          setGeneratingShareLink(false);
        }
      }

      if (!url) {
        showToast.error(isRTL ? 'الرابط غير متوفر' : 'Link not available');
        return;
      }

      // Build WhatsApp message
      let message = url;
      if (post?.title) {
        message = `${post.title}\n\n${message}`;
      }
      const captionText = post?.current_caption || post?.caption;
      if (captionText) {
        message = `${message}\n\n${captionText}`;
      }

      const clientWhatsApp = post?.client_whatsapp_number;
      const baseUrl = clientWhatsApp
        ? `https://wa.me/${clientWhatsApp.replace(/[^0-9]/g, '')}`
        : 'https://wa.me/';

      const whatsappUrl = `${baseUrl}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } catch (err) {
      console.error('WhatsApp share error:', err);
      showToast.error(isRTL ? 'فشل فتح واتساب' : 'Failed to open WhatsApp');
    }
  };

  // Download image/video function (improved)
  const handleDownloadImage = async () => {
    const imageUrl = post?.current_image_url || post?.image_url;
    if (!imageUrl) {
      showToast.error(isRTL ? 'لا توجد صورة للتحميل' : 'No image to download');
      return;
    }

    try {
      // Use getImageUrl to handle both local and external URLs
      let mediaUrl = getImageUrl(imageUrl);
      
      // Handle CORS issues by using a proxy or direct URL
      // If it's a relative URL or local file, use it directly
      if (mediaUrl.startsWith('/') || mediaUrl.startsWith('http://localhost') || mediaUrl.startsWith('https://')) {
        // Try to fetch with credentials
        const response = await fetch(mediaUrl, {
          method: 'GET',
          credentials: 'include',
          mode: 'cors',
        });

        if (!response.ok) {
          // If fetch fails due to CORS, try using the URL directly as a download link
          const a = document.createElement('a');
          a.href = mediaUrl;
          a.download = '';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          showToast.success(isRTL ? 'تم فتح الصورة' : 'Image opened');
          return;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Get file extension based on media type
        const mediaType = getMediaTypeFromUrl(mediaUrl);
        let extension = 'jpg';
        
        if (mediaType === 'video') {
          extension = blob.type.includes('mp4') ? 'mp4' : (blob.type.split('/')[1] || 'mp4');
        } else {
          // Try to get extension from blob type or URL
          if (blob.type && blob.type !== 'application/octet-stream') {
            extension = blob.type.split('/')[1] || 'jpg';
          } else {
            // Extract extension from URL
            const urlExtension = mediaUrl.split('.').pop()?.split('?')[0];
            extension = urlExtension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(urlExtension.toLowerCase()) 
              ? urlExtension 
              : 'jpg';
          }
        }
        
        const sanitizedTitle = (post.title || 'post').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
        a.download = `${sanitizedTitle}-${Date.now()}.${extension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast.success(isRTL ? 'تم التحميل بنجاح' : 'Downloaded successfully');
      } else {
        // Fallback: open in new tab
        const a = document.createElement('a');
        a.href = mediaUrl;
        a.download = '';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast.info(isRTL ? 'تم فتح الصورة في نافذة جديدة' : 'Image opened in new tab');
      }
    } catch (err) {
      console.error('Download error:', err);
      
      // Fallback: try to open the image URL directly
      try {
        const mediaUrl = getImageUrl(post.current_image_url);
        const a = document.createElement('a');
        a.href = mediaUrl;
        a.download = '';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast.info(isRTL ? 'تم فتح الصورة' : 'Image opened');
      } catch (fallbackErr) {
        console.error('Fallback download error:', fallbackErr);
        showToast.error(isRTL ? 'فشل تحميل الملف' : 'Failed to download file');
      }
    }
  };

  // Download caption as text file
  const handleDownloadCaption = () => {
    const caption = post?.current_caption || post?.caption;
    if (!caption) {
      showToast.error(isRTL ? 'لا يوجد عنوان للتحميل' : 'No caption to download');
      return;
    }

    try {
      const sanitizedTitle = (post.title || 'post').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
      const blob = new Blob([caption], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizedTitle}-caption-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success(isRTL ? 'تم تحميل العنوان' : 'Caption downloaded');
    } catch (err) {
      console.error('Download caption error:', err);
      showToast.error(isRTL ? 'فشل تحميل العنوان' : 'Failed to download caption');
    }
  };

  // Download all content as ZIP (image/video + caption + description)
  const handleDownloadAll = async () => {
    try {
      const imageUrl = post?.current_image_url || post?.image_url;
      const caption = post?.current_caption || post?.caption;
      
      // Using JSZip library - need to check if it's available or use alternative
      showToast.info(isRTL ? 'جاري إعداد الملفات...' : 'Preparing files...');
      
      // For now, download files separately
      // TODO: Implement ZIP download using JSZip or backend endpoint
      if (imageUrl) {
        await handleDownloadImage();
      }
      if (caption) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay
        handleDownloadCaption();
      }
      
      showToast.success(isRTL ? 'تم تحميل الملفات' : 'Files downloaded');
    } catch (err) {
      console.error('Download all error:', err);
      showToast.error(isRTL ? 'فشل تحميل الملفات' : 'Failed to download files');
    }
  };

  // Export post data as JSON
  const handleExportJSON = () => {
    try {
      const exportData = {
        id: post.id,
        title: post.title,
        description: post.description,
        caption: post.current_caption,
        rich_description: post.rich_description,
        platform: post.platform,
        content_type: post.content_type,
        status: post.status,
        image_url: post.current_image_url,
        client_name: post.client_name,
        created_at: post.created_at,
        updated_at: post.updated_at,
        specifications: post.specifications,
        product_details: post.product_details,
        article_details: post.article_details,
        seo_keywords: post.seo_keywords,
        meta_title: post.meta_title,
        meta_description: post.meta_description,
      };

      const sanitizedTitle = (post.title || 'post').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizedTitle}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success(isRTL ? 'تم تصدير البيانات' : 'Data exported');
    } catch (err) {
      console.error('Export JSON error:', err);
      showToast.error(isRTL ? 'فشل تصدير البيانات' : 'Failed to export data');
    }
  };

  // Export post data as CSV
  const handleExportCSV = () => {
    try {
      const headers = ['Field', 'Value'];
      const rows = [
        ['ID', post.id],
        ['Title', post.title || ''],
        ['Description', post.description || ''],
        ['Caption', post.current_caption || ''],
        ['Platform', post.platform || ''],
        ['Content Type', post.content_type || ''],
        ['Status', post.status || ''],
        ['Image URL', post.current_image_url || ''],
        ['Client Name', post.client_name || ''],
        ['Created At', post.created_at || ''],
        ['Updated At', post.updated_at || ''],
      ];

      // Handle rich_description (remove HTML tags for CSV)
      if (post.rich_description) {
        const plainText = post.rich_description.replace(/<[^>]*>/g, '');
        rows.push(['Rich Description', plainText]);
      }

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const sanitizedTitle = (post.title || 'post').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizedTitle}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success(isRTL ? 'تم تصدير البيانات' : 'Data exported');
    } catch (err) {
      console.error('Export CSV error:', err);
      showToast.error(isRTL ? 'فشل تصدير البيانات' : 'Failed to export data');
    }
  };

  // Copy text to clipboard function
  const handleCopyText = async (text, type) => {
    if (!text || !text.trim()) {
      showToast.error(isRTL ? 'لا يوجد نص للنسخ' : 'No text to copy');
      return;
    }

    try {
      // Remove HTML tags if present (for rich_description)
      const plainText = text.replace(/<[^>]*>/g, '').trim();
      await navigator.clipboard.writeText(plainText);
      setCopiedText(type);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const plainText = text.replace(/<[^>]*>/g, '').trim();
      const textArea = document.createElement('textarea');
      textArea.value = plainText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedText(type);
      setTimeout(() => setCopiedText(null), 2000);
    }
  };

  // Load existing share token if available
  useEffect(() => {
    if (post?.share_token) {
      const url = `${window.location.origin}/post/${post.share_token}`;
      setShareUrl(url);
    }
  }, [post?.share_token]);

  const handleAddComment = async (commentData) => {
    try {
      await commentsAPI.create(id, commentData);
      await fetchPostData();
      showToast.success(isRTL ? 'تم إضافة التعليق بنجاح!' : 'Comment added successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if this post content type supports video
    const isVideoPost = post?.content_type && isVideoContent(post.content_type);
    const isVideoFile = file.type.startsWith('video/');
    const isImageFile = file.type.startsWith('image/');

    // Validate file type
    if (!isValidMedia(file)) {
      showToast.error(isRTL 
        ? 'نوع الملف غير صحيح. يرجى رفع صورة أو فيديو' 
        : 'Invalid file type. Please upload an image or video');
      return;
    }

    // Check if file type matches post content type
    if (isVideoPost && !isVideoFile) {
      showToast.error(isRTL 
        ? 'هذا البوست يتطلب فيديو. يرجى رفع ملف فيديو.' 
        : 'This post requires a video. Please upload a video file.');
      return;
    }

    if (!isVideoPost && isVideoFile) {
      showToast.error(isRTL 
        ? 'هذا البوست يتطلب صورة. يرجى رفع ملف صورة.' 
        : 'This post requires an image. Please upload an image file.');
      return;
    }

    // Validate file size based on type
    const maxSize = isVideoFile ? 100 * 1024 * 1024 : 5 * 1024 * 1024; // 100MB for video, 5MB for image
    if (file.size > maxSize) {
      const maxSizeMB = isVideoFile ? 100 : 5;
      showToast.error(isRTL 
        ? `حجم الملف يجب أن يكون أقل من ${maxSizeMB}MB` 
        : `File size must be less than ${maxSizeMB}MB`);
      return;
    }

    try {
      setUploading(true);
      
      // Upload file (works for both images and videos)
      const uploadResponse = await uploadAPI.uploadImage(file);
      const fileUrl = uploadResponse.data.url;

      // Store file URL and show notes modal
      const isCarousel = post?.content_type === 'carousel';
      
      if (isCarousel) {
        // For carousel posts, add to pendingCarouselItems instead
        const newItem = {
          url: fileUrl,
          type: isVideoFile ? 'video' : 'image',
          order: (pendingCarouselItems?.length || 0) + 1
        };
        setPendingCarouselItems([...(pendingCarouselItems || []), newItem]);
        // Don't set pendingImageUrl for carousel
      } else {
        // For regular posts, use pendingImageUrl
        setPendingImageUrl(fileUrl);
      }
      
      setShowVersionNotesModal(true);
      e.target.value = ''; // Reset file input
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitVersion = async () => {
    const isCarousel = post?.content_type === 'carousel';
    const hasCarouselItems = pendingCarouselItems && pendingCarouselItems.length > 0;
    
    if (!pendingImageUrl && !hasCarouselItems) {
      if (isCarousel) {
        showToast.error(isRTL ? 'يرجى إضافة عنصر واحد على الأقل للكاروسيل' : 'Please add at least one carousel item');
      } else {
        showToast.error(isRTL ? 'يرجى رفع صورة' : 'Please upload an image');
      }
      return;
    }

    try {
      setUploading(true);
      
      // For carousel posts, send carousel_items
      if (isCarousel && hasCarouselItems) {
        // Ensure all items have url
        const validItems = pendingCarouselItems.filter(item => item.url);
        if (validItems.length === 0) {
          showToast.error(isRTL ? 'يرجى إضافة عناصر صالحة للكاروسيل' : 'Please add valid carousel items');
          return;
        }
        
      await postsAPI.createVersion(id, {
          carousel_items: validItems,
        caption: post.current_caption || '',
          notes: versionNotes.trim() || '',
        });
      } else {
        // For regular posts, use image_url
        await postsAPI.createVersion(id, {
          image_url: pendingImageUrl,
          caption: post.current_caption || '',
          notes: versionNotes.trim() || '',
        });
      }

      await fetchPostData();
      setShowUploadImage(false);
      setShowVersionNotesModal(false);
      setVersionNotes('');
      setPendingImageUrl(null);
      setPendingCarouselItems([]);
      showToast.success(isRTL ? 'تم رفع النسخة الجديدة بنجاح!' : 'New version uploaded successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleCancelVersionNotes = () => {
    setShowVersionNotesModal(false);
    setVersionNotes('');
    setPendingImageUrl(null);
    setPendingCarouselItems([]);
  };

  // Check if agency has AI generation feature
  const hasAIGeneration = React.useMemo(() => {
    if (!agency?.plan_features) return false;
    const features = Array.isArray(agency.plan_features) 
      ? agency.plan_features 
      : (typeof agency.plan_features === 'string' 
          ? JSON.parse(agency.plan_features) 
          : []);
    
    return features.some(f => {
      const featureName = typeof f === 'string' ? f : (f.name || f);
      return featureName && (
        featureName.toLowerCase().includes('ai') ||
        featureName.toLowerCase().includes('generation') ||
        featureName.toLowerCase().includes('artificial intelligence')
      );
    });
  }, [agency?.plan_features]);

  const handleGenerateAICaption = async () => {
    if (!post) return;

    // Check if agency has AI generation feature
    if (!hasAIGeneration) {
      showToast.error(isRTL 
        ? 'ميزة AI Generation غير متاحة في باقاتك الحالية. يرجى الترقية للوصول إلى هذه الميزة.'
        : 'AI Generation feature is not available in your current plan. Please upgrade to access this feature.'
      );
      return;
    }

    try {
      setGeneratingAICaption(true);
      
      const response = await aiAPI.generateCaption(id, {
        language: isRTL ? 'arabic' : 'english',
        tone: 'professional',
      });

      const generatedCaption = response.data.caption;
      
      // Set the generated caption and open edit mode
      setNewCaption(generatedCaption);
      setShowEditCaption(true);
      
      showToast.success(isRTL ? 'تم توليد العنوان بنجاح! يمكنك تعديله قبل الحفظ.' : 'Caption generated successfully! You can edit it before saving.');
    } catch (err) {
      console.error('AI generation error:', err);
      const errorMessage = handleApiError(err);
      if (errorMessage.includes('not configured')) {
        showToast.error(isRTL 
          ? 'خدمة AI غير مفعلة. يرجى إضافة GEMINI_API_KEY في إعدادات السيرفر.'
          : 'AI service not configured. Please add GEMINI_API_KEY to server environment variables.'
        );
      } else if (errorMessage.includes('not available') || errorMessage.includes('upgrade')) {
        showToast.error(errorMessage);
      } else {
        showToast.error(errorMessage);
      }
    } finally {
      setGeneratingAICaption(false);
    }
  };

  const handleEditCaption = async () => {
    if (!newCaption.trim()) {
      showToast.error(isRTL ? 'يرجى إدخال العنوان' : 'Please enter a caption');
      return;
    }

    if (!post) return; // Safety check

    try {
      setEditingCaption(true);

      const isCarousel = post.content_type === 'carousel';
      const hasCarouselItems =
        Array.isArray(post.current_carousel_items) &&
        post.current_carousel_items.length > 0;

      if (isCarousel && hasCarouselItems) {
        // Carousel post: keep all items, only update caption
        await postsAPI.createVersion(id, {
          carousel_items: post.current_carousel_items,
          caption: newCaption,
          notes: isRTL ? 'تم تحديث العنوان' : 'Caption updated',
        });
      } else if (post.current_image_url) {
        // Regular post with single image
        await postsAPI.createVersion(id, {
          image_url: post.current_image_url,
          caption: newCaption,
          notes: isRTL ? 'تم تحديث العنوان' : 'Caption updated',
        });
      } else {
        // Post without image yet – caption only
        await postsAPI.createVersion(id, {
          image_url: '',
          caption: newCaption,
          notes: isRTL ? 'تم إضافة العنوان' : 'Caption added',
        });
      }

      await fetchPostData();
      setShowEditCaption(false);
      setNewCaption('');
      showToast.success(isRTL ? 'تم تحديث العنوان بنجاح!' : 'Caption updated successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setEditingCaption(false);
    }
  };

  const handleEditPost = async () => {
    if (!editFormData.title.trim()) {
      showToast.error(isRTL ? 'العنوان مطلوب' : 'Title is required');
      return;
    }

    try {
      setEditingPost(true);
      await postsAPI.update(id, {
        title: editFormData.title.trim(),
        description: editFormData.description.trim() || null,
      });
      await fetchPostData();
      setShowEditPost(false);
      showToast.success(isRTL ? 'تم تحديث البوست بنجاح!' : 'Post updated successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setEditingPost(false);
    }
  };

  const handleDeleteMedia = async () => {
    const confirmed = await showConfirm(
      isRTL 
        ? 'هل أنت متأكد من حذف الصورة/الفيديو؟ سيتم إنشاء نسخة جديدة بدون ملف.' 
        : 'Are you sure you want to delete the media? A new version will be created without the file.',
      isRTL
    );

    if (!confirmed) return;

    try {
      setActionLoading(true);
      // Create a new version without image_url
      await postsAPI.createVersion(id, {
        image_url: '',
        caption: post.current_caption || '',
        notes: isRTL ? 'تم حذف الصورة/الفيديو' : 'Media deleted',
      });
      await fetchPostData();
      showToast.success(isRTL ? 'تم حذف الملف بنجاح!' : 'Media deleted successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveCarouselItems = async () => {
    if (!manageCarouselItems || manageCarouselItems.length === 0) {
      showToast.error(isRTL ? 'يرجى إضافة عنصر واحد على الأقل' : 'Please add at least one item');
      return;
    }

    const validItems = manageCarouselItems.filter(item => item.url).map((item, index) => ({
      ...item,
      order: index + 1
    }));

    if (validItems.length === 0) {
      showToast.error(isRTL ? 'يرجى إضافة عناصر صالحة' : 'Please add valid items');
      return;
    }

    try {
      setSavingCarouselItems(true);
      await postsAPI.createVersion(id, {
        carousel_items: validItems,
        caption: post.current_caption || '',
        notes: isRTL ? 'تم تحديث عناصر الكاروسيل' : 'Carousel items updated',
      });
      await fetchPostData();
      setEditingCarouselItems(false);
      showToast.success(isRTL ? 'تم حفظ العناصر بنجاح!' : 'Items saved successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setSavingCarouselItems(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4 font-semibold">Error loading post</p>
        <p className="text-gray-600 mb-4 text-sm">{error}</p>
        <div className="space-x-3">
          <Button onClick={fetchPostData} variant="primary">
            Try Again
          </Button>
          <Button onClick={() => navigate(-1)} variant="secondary">
            ← Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Post not found</p>
        <Button onClick={() => navigate(-1)} variant="secondary">
          ← Go Back
        </Button>
      </div>
    );
  }

  // Check if user can upload (designer or admin) - MOVED HERE after post check
  const canUploadImage = (isDesigner || isAdmin) && 
    (post.assigned_designer_id === user?.id || isAdmin);
  
  // Check if user can edit caption (writer or admin) - MOVED HERE after post check
  const canEditCaption = (isWriter || isAdmin) && 
    (post.assigned_writer_id === user?.id || isAdmin);

  // Debug: Log post data
  console.log('Rendering post:', post);
  console.log('Post ID:', id);
  console.log('Post title:', post.title);

  return (
    <div className="space-y-6">
      {/* Debug info (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="p-3 bg-gray-100 rounded text-xs font-mono">
          <strong>Debug:</strong> Post ID: {id} | Has Image: {post.current_image_url ? 'Yes' : 'No'} | 
          Comments: {comments?.length || 0} | Versions: {versions?.length || 0}
        </div>
      )}

      {/* Back to Client Link */}
          {post.client_id && (
            <Link
              to={`/clients/${post.client_id}`}
          className={`text-xs font-semibold text-sky-700 hover:text-sky-800 inline-flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}
            >
          <span className={`${isRTL ? 'rotate-180' : ''}`}>←</span>
          {isRTL ? 'العودة إلى' : 'Back to'} {post.client_name || (isRTL ? 'العميل' : 'Client')}
            </Link>
          )}

      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className={`flex items-start justify-between gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
          {!showEditPost ? (
            <>
                <div className={`flex items-start justify-between gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="min-w-0">
                    <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <h1
                        className="text-2xl md:text-3xl font-extrabold text-slate-900 truncate max-w-2xl"
                        title={post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
                      >
                        {post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
                      </h1>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowEditPost(true);
                            setEditFormData({
                              title: post.title || '',
                              description: post.description || '',
                            });
                          }}
                          className="text-xs font-medium text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline"
                        >
                          {isRTL ? 'تعديل العنوان' : 'Edit title'}
                        </button>
                      )}
                    </div>
          {post.description && (
                      <p className="text-slate-600 mt-1 text-sm">
                        {post.description}
                      </p>
                    )}
                    <div className="mt-3 flex flex-col gap-2 text-[11px] text-slate-600">
                      <div className="flex flex-wrap items-center gap-2">
                        {post.status && <StatusBadge status={post.status} />}
                        {post.scheduled_at && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700">
                            <Calendar className="w-3 h-3" />
                            {isRTL ? 'مجدول للنشر' : 'Scheduled'} {formatDate(post.scheduled_at)}
                          </span>
                        )}
                        {post.published_at && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-green-700">
                            <Check className="w-3 h-3" />
                            {isRTL ? 'تم النشر' : 'Published'} {formatDate(post.published_at)}
                          </span>
                        )}
                        {post.created_at && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 border border-slate-200">
                            🗓 {isRTL ? 'تاريخ الإنشاء' : 'Created'} {formatDate(post.created_at)}
                          </span>
                        )}
                        {post.current_version && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/70 border border-slate-200">
                            🧩 {isRTL ? 'النسخة' : 'Version'} v{post.current_version}
                          </span>
        )}
      </div>

                      {/* Designer & Writer (editable من الهيدر) */}
                      <div className={`flex flex-wrap gap-3 text-[11px] ${isRTL ? 'justify-end' : 'justify-start'}`}>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-500">
                             {isRTL ? 'المصمم' : 'Designer'}:
                          </span>
                          {!editingDesigner ? (
                            <>
                              <span className="font-medium text-slate-800">
                                {post.designer_name || (isRTL ? 'غير معين' : 'Not assigned')}
                              </span>
                              {isAdmin && (
                <button
                  onClick={() => setEditingDesigner(true)}
                                  className="text-sky-600 hover:text-sky-700 text-[11px]"
                  title={isRTL ? 'تعديل' : 'Edit'}
                >
                  ✏️
                </button>
              )}
                            </>
                ) : (
                  <select
                    value={post.assigned_designer_id || ''}
                    onChange={async (e) => {
                      const newDesignerId = e.target.value || null;
                      try {
                        setUpdatingAssignment(true);
                        await postsAPI.update(id, { assigned_designer_id: newDesignerId });
                        await fetchPostData();
                        setEditingDesigner(false);
                                  showToast.success(
                                    isRTL ? 'تم تحديث المصمم بنجاح!' : 'Designer updated successfully!'
                                  );
                      } catch (err) {
                        showToast.error(handleApiError(err));
                      } finally {
                        setUpdatingAssignment(false);
                      }
                    }}
                    disabled={updatingAssignment}
                              className="input h-7 px-2 py-0 text-[11px]"
                    autoFocus
                  >
                    <option value="">{isRTL ? 'غير معين' : 'Not assigned'}</option>
                              {designers.map((designer) => (
                      <option key={designer.id} value={designer.id}>
                        {designer.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

                        <div className="flex items-center gap-1">
                          <span className="text-slate-500">
                            {isRTL ? 'الكاتب' : 'Writer'}:
                          </span>
                          {!editingWriter ? (
                            <>
                              <span className="font-medium text-slate-800">
                                {post.writer_name || (isRTL ? 'غير معين' : 'Not assigned')}
                              </span>
                              {isAdmin && (
                <button
                  onClick={() => setEditingWriter(true)}
                                  className="text-sky-600 hover:text-sky-700 text-[11px]"
                  title={isRTL ? 'تعديل' : 'Edit'}
                >
                  ✏️
                </button>
              )}
                            </>
                ) : (
                  <select
                    value={post.assigned_writer_id || ''}
                    onChange={async (e) => {
                      const newWriterId = e.target.value || null;
                      try {
                        setUpdatingAssignment(true);
                        await postsAPI.update(id, { assigned_writer_id: newWriterId });
                        await fetchPostData();
                        setEditingWriter(false);
                                  showToast.success(
                                    isRTL ? 'تم تحديث الكاتب بنجاح!' : 'Writer updated successfully!'
                                  );
                      } catch (err) {
                        showToast.error(handleApiError(err));
                      } finally {
                        setUpdatingAssignment(false);
                      }
                    }}
                    disabled={updatingAssignment}
                              className="input h-7 px-2 py-0 text-[11px]"
                    autoFocus
                  >
                    <option value="">{isRTL ? 'غير معين' : 'Not assigned'}</option>
                              {writers.map((writer) => (
                      <option key={writer.id} value={writer.id}>
                        {writer.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
                  </div>
          </div>
              </>
            ) : (
              <Card className="p-4 bg-white/70 border-slate-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {isRTL ? 'العنوان' : 'Title'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editFormData.title}
                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                      className="input w-full"
                      placeholder={isRTL ? 'عنوان البوست' : 'Post title'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {isRTL ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      className="textarea w-full"
                      rows="3"
                      placeholder={isRTL ? 'وصف البوست (اختياري)' : 'Post description (optional)'}
                    />
                  </div>
                  <div className={`flex gap-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                    <Button
                      variant="primary"
                      onClick={handleEditPost}
                      loading={editingPost}
                      disabled={editingPost}
                      className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                    >
                      {isRTL ? 'حفظ' : 'Save'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowEditPost(false);
                        setEditFormData({ title: '', description: '' });
                      }}
                      disabled={editingPost}
                    >
                      {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                </div>
                </div>
              </Card>
              )}
            </div>

          {/* Header Actions */}
          <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Client actions: approve / request changes */}
            <div className={`flex flex-col sm:flex-row gap-2 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              {isClient && post.status === 'pending' && (
                <>
                  <Button
                    variant="secondary"
                    onClick={handleRequestChanges}
                    disabled={actionLoading}
                  >
                    {isRTL ? 'طلب تعديلات' : 'Request Changes'}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleApprove}
                    loading={actionLoading}
                    disabled={actionLoading}
                    className="bg-gradient-to-r from-emerald-600 to-sky-600 hover:from-emerald-700 hover:to-sky-700 text-white shadow-lg shadow-emerald-500/25"
                  >
                    {isRTL ? 'تعميد' : 'Approve'}
                  </Button>
                </>
              )}

              {(isDesigner || isWriter) && post.status === 'needs_changes' && (
                <Button
                  variant="primary"
                  onClick={handleMarkAsReadyForReview}
                  loading={actionLoading}
                  disabled={actionLoading}
                  className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                >
                  {isRTL ? '✓ جاهز للمراجعة' : '✓ Mark as Ready for Review'}
                </Button>
              )}
            </div>

            {/* Admin / staff actions: schedule */}
            {!isClient && (
              <div className="flex flex-col gap-2 w-40">
                {(post?.status === 'approved' || post?.status === 'scheduled') && (
                  <Button
                    onClick={() => setShowScheduleModal(true)}
                    disabled={actionLoading}
                    variant="primary"
                    size="sm"
                    className="w-full bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
                  >
                    <Calendar className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {post?.status === 'scheduled'
                      ? (isRTL ? 'تعديل الجدولة' : 'Edit Schedule')
                      : (isRTL ? 'جدولة البوست' : 'Schedule Post')}
                  </Button>
                )}
              </div>
            )}
          </div>
            </div>
        </Card>

      {/* Share & Download Actions - تحت الهيدر مباشرة */}
      <Card className="p-4 bg-gradient-to-r from-blue-50 via-white to-purple-50 border-blue-100">
        <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'justify-end' : 'justify-start'}`}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Share2 className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-900">
              {isRTL ? 'مشاركة وتحميل' : 'Share & Download'}
            </h3>
          </div>
          
          <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {(isAdmin || isDesigner || isWriter) && (
              <Button
                onClick={handleCopyShareLink}
                disabled={generatingShareLink}
                variant="primary"
                size="sm"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30"
              >
                <Share2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {shareUrlCopied
                  ? isRTL
                    ? 'تم نسخ الرابط'
                    : 'Link Copied'
                  : isRTL
                  ? 'نسخ رابط المشاركة'
                  : 'Copy Share Link'}
              </Button>
            )}
            <Button
              onClick={handleShareWhatsApp}
              variant="outline"
              size="sm"
              className="bg-white hover:bg-green-50 border-green-200"
            >
              {/* WhatsApp Icon */}
              <svg className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {post?.client_whatsapp_number
                ? (isRTL ? 'إرسال للعميل على واتساب' : 'Send to client on WhatsApp')
                : (isRTL ? 'مشاركة عبر واتساب' : 'Share via WhatsApp')}
            </Button>
          </div>
        </div>
      </Card>

      {/* Post Meta Info & Platform card تم إخفاؤها بعد نقل أهم المعلومات للهيدر والتخطيط الجديد */}

      {/* Version History (Collapsible) */}
      {showVersionHistory && versions && versions.length > 0 && (
        <VersionHistory versions={versions} currentVersionId={post.current_version_id} />
      )}

      {/* Main Content: Image + Caption + Comments + Actions/Share/Download */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Image Viewer (2/3 width on large screens) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image with Annotations */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Design</h2>
              {canUploadImage && (
                <div className="flex items-center space-x-2">
                  {!showUploadImage ? (
                    <label className="btn btn-secondary text-sm cursor-pointer">
                      📤 {isRTL ? 'رفع ملف' : 'Upload Media'}
                      <input
                        type="file"
                        accept="image/*,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
                        onChange={handleUploadImage}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowUploadImage(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              )}
            </div>
            {uploading && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">{isRTL ? 'جاري الرفع...' : 'Uploading...'}</p>
              </div>
            )}
            {(post.current_image_url || (post.content_type === 'carousel' && post.current_carousel_items && post.current_carousel_items.length > 0)) ? (
              <div className="space-y-4">
                <div className="relative">
                <ImageViewer
                    imageUrl={post.current_image_url ? getImageUrl(post.current_image_url) : null}
                    mediaType={post.current_image_url ? getMediaTypeFromUrl(post.current_image_url) : null}
                    carouselItems={post.current_carousel_items || []}
                  comments={comments || []}
                  onAddComment={handleAddComment}
                  canComment={true}
                />
                  {/* Replace/Delete Media Buttons (only if post is not approved) */}
                  {canUploadImage && post.content_type !== 'carousel' && post.current_image_url && post.status !== 'approved' && (
                    <div className={`mt-2 flex gap-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                      <label className="btn btn-secondary text-sm cursor-pointer">
                        🔄 {isRTL ? 'استبدال' : 'Replace'}
                        <input
                          type="file"
                          accept="image/*,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
                          onChange={handleUploadImage}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleDeleteMedia}
                        disabled={actionLoading}
                      >
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        {isRTL ? 'حذف' : 'Delete'}
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Carousel Items Management (only for carousel posts) */}
                {post.content_type === 'carousel' && canUploadImage && (
                  <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {isRTL ? 'إدارة عناصر الكاروسيل' : 'Manage Carousel Items'}
                      </h3>
                      {!editingCarouselItems && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setEditingCarouselItems(true);
                            setManageCarouselItems(post.current_carousel_items || []);
                          }}
                        >
                          {isRTL ? 'تعديل العناصر' : 'Edit Items'}
                        </Button>
                      )}
                    </div>
                    
                    {editingCarouselItems ? (
                      <div className="space-y-4">
                        <CarouselItemsManager
                          items={manageCarouselItems}
                          onChange={setManageCarouselItems}
                          disabled={savingCarouselItems}
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            onClick={handleSaveCarouselItems}
                            loading={savingCarouselItems}
                            disabled={savingCarouselItems}
                          >
                            {isRTL ? 'حفظ' : 'Save'}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setEditingCarouselItems(false);
                              setManageCarouselItems(post.current_carousel_items || []);
                            }}
                            disabled={savingCarouselItems}
                          >
                            {isRTL ? 'إلغاء' : 'Cancel'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        {post.current_carousel_items && post.current_carousel_items.length > 0 ? (
                          <p>
                            {isRTL 
                              ? `يحتوي الكاروسيل على ${post.current_carousel_items.length} عنصر. اضغط "تعديل العناصر" لإدارة العناصر.`
                              : `Carousel has ${post.current_carousel_items.length} items. Click "Edit Items" to manage them.`
                            }
                          </p>
                        ) : (
                          <p className="text-gray-500">
                            {isRTL ? 'لا توجد عناصر في الكاروسيل' : 'No carousel items yet'}
                          </p>
                        )}
                      </div>
                    )}
                  </Card>
                )}
              </div>
            ) : (
              <div className="aspect-video bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                <p className="text-gray-500 mb-2">{isRTL ? 'لم يتم رفع أي ملف بعد' : 'No media uploaded yet'}</p>
                {canUploadImage && (
                  <label className="btn btn-primary text-sm cursor-pointer">
                    📤 {isRTL ? 'رفع أول ملف' : 'Upload First Media'}
                    <input
                      type="file"
                      accept="image/*,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
                      onChange={handleUploadImage}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            )}
          </Card>

          {/* تحت التصميم: Product/Article Details أو Caption حسب نوع المحتوى */}
          {(post.content_type === 'product' || post.content_type === 'blog') ? (
            <ProductArticleDetailsCard 
              key={post.id}
              post={post} 
              onUpdate={fetchPostData}
            />
          ) : (
            /* Caption - Hide for banners/hero/header */
           post.content_type !== 'banner' && 
           post.content_type !== 'hero' && 
           post.content_type !== 'header' && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {isRTL ? 'العنوان' : 'Caption'}
                </h2>
              {canEditCaption && !showEditCaption && (
                <div className="flex gap-2">
                  {hasAIGeneration ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleGenerateAICaption}
                    loading={generatingAICaption}
                    disabled={generatingAICaption}
                  >
                    {isRTL ? '✨ توليد بـ AI' : '✨ Generate with AI'}
                  </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      title={isRTL ? 'ميزة AI غير متاحة في باقاتك الحالية' : 'AI feature not available in your current plan'}
                    >
                      {isRTL ? '✨ توليد بـ AI (غير متاح)' : '✨ Generate with AI (Not Available)'}
                    </Button>
                  )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowEditCaption(!showEditCaption);
                    setNewCaption(post.current_caption || '');
                  }}
                >
                    {isRTL ? '✏️ تعديل' : '✏️ Edit'}
                  </Button>
                </div>
              )}
              {canEditCaption && showEditCaption && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowEditCaption(false);
                    setNewCaption('');
                  }}
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
              )}
            </div>
            {showEditCaption ? (
              <div className="space-y-3">
                <textarea
                  value={newCaption}
                  onChange={(e) => setNewCaption(e.target.value)}
                  placeholder="Enter caption for this post..."
                  className="textarea"
                  rows="5"
                />
                <div className="flex space-x-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleEditCaption}
                    loading={editingCaption}
                    disabled={editingCaption}
                  >
                    {isRTL ? 'حفظ العنوان' : 'Save Caption'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowEditCaption(false);
                      setNewCaption('');
                    }}
                  >
                    {isRTL ? 'إلغاء' : 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              post.current_caption ? (
                <p className="text-gray-700 whitespace-pre-wrap">{post.current_caption}</p>
              ) : (
                <div>
                  <p className="text-gray-500 italic mb-2">No caption yet</p>
                  {canEditCaption && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowEditCaption(true);
                        setNewCaption('');
                      }}
                    >
                      Add Caption
                    </Button>
                  )}
                </div>
              )
            )}
          </Card>
          )
          )}

          {/* Notes */}
          {post.current_notes && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{post.current_notes}</p>
            </Card>
          )}
        </div>

        {/* Right Column: Platform Preview + Main Actions + Share & Download + Comments */}
        <div className="lg:col-span-1 space-y-4">
          {/* Platform Preview في العمود اليمين */}
          {post.platform && (
            <Card className="p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {isRTL ? 'معاينة على المنصة' : 'Platform Preview'}
              </h3>
              <div className="flex justify-center">
                <PlatformPreview
                  platform={post.platform}
                  contentType={post.content_type}
                  imageUrl={post.current_image_url ? getImageUrl(post.current_image_url) : null}
                  caption={post.current_caption}
                  title={post.title}
                  description={post.description}
                  richDescription={post.rich_description}
                  productDetails={post.product_details}
                  articleDetails={post.article_details}
                  specifications={post.specifications}
                  carouselItems={post.current_carousel_items || []}
                  username={
                    post.platform === 'instagram' && post.client_social_media_accounts?.instagram
                      ? post.client_social_media_accounts.instagram.replace('@', '')
                      : 'username'
                  }
                />
              </div>
            </Card>
          )}

          {/* Comments */}
          <CommentsList
            comments={comments || []}
            postId={id}
            onAddComment={handleAddComment}
            onRefresh={fetchPostData}
          />
        </div>
      </div>

      {/* Approval Status Section (for approved posts) */}
      {post.status === 'approved' && (
        <>
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Approved</h3>
              <p className="text-sm text-green-700 mt-1">
                This post was approved by {post.approved_by_name} on{' '}
                {formatDate(post.approved_at)}
              </p>
            </div>
          </div>
        </Card>
        </>
      )}

      {/* Change Request Modal */}
      {showChangeRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {isRTL ? 'طلب تعديلات' : 'Request Changes'}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                {isRTL 
                  ? 'يرجى توضيح التعديلات المطلوبة على التصميم:' 
                  : 'Please explain what changes are needed on the design:'}
              </p>
              <textarea
                value={changeRequestText}
                onChange={(e) => setChangeRequestText(e.target.value)}
                placeholder={isRTL 
                  ? 'مثال: يرجى تغيير اللون إلى أزرق، أو جعل الخط أكبر...' 
                  : 'Example: Please change the color to blue, or make the font larger...'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm sm:text-base"
                rows="6"
                autoFocus
              />
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={handleSubmitChangeRequest}
                  loading={actionLoading}
                  disabled={actionLoading || !changeRequestText.trim()}
                  className="w-full sm:flex-1"
                >
                  {isRTL ? 'إرسال' : 'Submit'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancelChangeRequest}
                  disabled={actionLoading}
                  className="w-full sm:flex-1"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Post Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {isRTL ? 'جدولة النشر' : 'Schedule Post'}
                </h3>
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduledDate('');
                    setScheduledTime('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                {isRTL 
                  ? 'حدد التاريخ والوقت المطلوبين لنشر هذا البوست:' 
                  : 'Select the date and time when you want to publish this post:'}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'التاريخ' : 'Date'}
                  </label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'الوقت' : 'Time'}
                  </label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>

                {post?.scheduled_at && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      {isRTL ? 'الجدولة الحالية:' : 'Current schedule:'}{' '}
                      <strong>{formatDate(post.scheduled_at)}</strong>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  variant="primary"
                  onClick={handleSchedule}
                  loading={scheduling}
                  disabled={scheduling || !scheduledDate || !scheduledTime}
                  className="w-full sm:flex-1"
                >
                  <Clock className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'حفظ الجدولة' : 'Save Schedule'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduledDate('');
                    setScheduledTime('');
                  }}
                  disabled={scheduling}
                  className="w-full sm:flex-1"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version Notes Modal */}
      {showVersionNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`bg-white rounded-lg shadow-xl w-full ${post?.content_type === 'carousel' ? 'max-w-4xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}>
            <div className="p-4 sm:p-6">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                {isRTL ? 'إضافة ملاحظات على النسخة' : 'Add Version Notes'}
              </h3>
              
              {/* Carousel Items Manager (only for carousel posts) */}
              {post?.content_type === 'carousel' && (
                <div className="mb-4">
                  <CarouselItemsManager
                    items={pendingCarouselItems}
                    onChange={setPendingCarouselItems}
                    disabled={uploading}
                  />
                </div>
              )}

              <p className="text-sm sm:text-base text-gray-600 mb-4">
                {isRTL 
                  ? 'أضف ملاحظات حول هذه النسخة (اختياري):' 
                  : 'Add notes about this version (optional):'}
              </p>
              <textarea
                value={versionNotes}
                onChange={(e) => setVersionNotes(e.target.value)}
                placeholder={isRTL 
                  ? 'مثال: تغيير الألوان، تحديث النص...' 
                  : 'Example: Changed colors, updated text...'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm sm:text-base"
                rows="4"
                autoFocus
              />
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={handleSubmitVersion}
                  loading={uploading}
                  disabled={uploading}
                  className="w-full sm:flex-1"
                >
                  {isRTL ? 'إضافة النسخة' : 'Add Version'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancelVersionNotes}
                  disabled={uploading}
                  className="w-full sm:flex-1"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostReviewPage;


